/**
 * Data o službách Stable Records — lokalizovaná verze (CS + EN).
 *
 * Pole `id`, `src`, `images`, `price`, `availability` jsou sdílená napříč jazyky
 * (structure / assets). Pole `name`, `headline`, `description`, `bullets`,
 * `seoTitle`, `seoDescription` existují pro oba jazyky.
 *
 * Výběr jazyka: použij helper `getServicesData(lang)` (vrací pole ve správném jazyce).
 * Default export je česká verze (zpětná kompatibilita + test fallback).
 */

const servicesDataCs = [
  {
    id: 'ubytovani',
    name: 'Ubytování',
    seoTitle: 'Ubytování ve studiu u Prahy | Stable Records — v ceně nahrávání',
    seoDescription: 'Ubytování pro až 7 osob přímo v nahrávacím studiu u Prahy. V ceně full service — bez příplatku. Gril, bazén, klidné prostředí 20 km od Prahy.',
    headline: 'Ubytování přímo ve studiu — v ceně full service',
    description: 'Ubytování pro až 7 lidí přímo ve studiu, bez příplatku u full service. Pokoje mají všechno, co potřebuješ. K dispozici je zahrada s grilem a bazén — po celém dni ve studiu se hodí.',
    price: 'Cena na vyžádání',
    availability: 'Dostupné na vyžádání',
    images: [
      '/images/ubytko/grill.webp',
      '/images/ubytko/postele.webp',
      '/images/ubytko/koupelna.webp',
      '/images/ubytko/bar.webp',
    ],
    bullets: [
      'Ubytování pro až 7 osob',
      'Vybavené pokoje s veškerým komfortem',
      'Možnost venkovního grilování',
      'Bazén',
      'All-inclusive — je možné zajistit i stravování',
      'Soukromí a klid',
      'Ideální pro kapely a hudební týmy',
      'Přímo ve studiu, bez nutnosti cestovat',
    ],
    src: '/images/ubytko/ubytko.webp',
  },
  {
    id: 'mix_mastering',
    name: 'Mix a Mastering',
    seoTitle: 'Mix a mastering Praha — zkušenost 15 let | Stable Records',
    seoDescription: 'Profesionální mix a mastering pro kapely i sólisty u Prahy (Praha-západ, Středočeský kraj). Přes 15 let zkušeností. Zdarma ukázkový mix do 24 h — napište nám.',
    headline: 'Mix a mastering Praha — od surových stop k hotovému zvuku',
    description: 'Při mixu srovnám jednotlivé stopy tak, aby každý nástroj měl svůj prostor a celek zněl přirozeně. Mastering je poslední krok — připravím výsledný zvuk tak, aby fungoval na Spotify, v autě i na sluchátkách. <br> Před mixem pečlivě vyčistím a zedituji stopy — timing, ladění, šumy. Pracuju v Logic s pluginy od SSL, Universal Audio a Slate Digital. Na mastering používám Izotope Ozone 10 a Slate Digital FGX 2. Mixu a masteringu se věnuju přes deset let. Kompletní seznam vybavení najdeš na stránce <a href="/vybaveni"><strong>Vybavení studia</strong></a>.',
    price: 'Cena na vyžádání',
    availability: 'Dostupné na vyžádání',
    images: [
      '/images/services/drums.webp',
      '/images/services/mixnmaster.webp',
      '/images/services/mix.webp',
      '/images/services/mix2.webp',
    ],
    bullets: [
      'Precizní editace a mix zvukových stop',
      'Práce v DAW Logic',
      'Špičkové pluginy (Universal Audio, Slate Digital, SSL)',
      'Masteringové nástroje (Izotope Ozone 10, Slate Digital FGX 2)',
      'Profesionální zvuk připraven pro streamovací platformy i fyzická média',
      'Více než 10 let zkušeností s mixem a masteringem',
      'Důraz na detail a kvalitu',
      'Vysvětlím ti mixovací a masteringové postupy, pokud tě to zajímá',
      'Společně hledáme zvuk, který ti sedí',
      'Ukážu ti, na co se zaměřit při poslechu a jak popsat, co chceš',
    ],
    src: '/images/services/mix.webp',
  },
  {
    id: 'nahravani',
    name: 'Nahrávání',
    seoTitle: 'Nahrávání kapel a hudby — Praha a okolí | Stable Records',
    seoDescription: 'Nahrávání kapel, vokálů, bicích i celých sestav 15 min od Prahy. Neumann U87, akusticky řešená místnost, ubytování pro kapely. Vhodné i pro první nahrávání — vysvětlíme vám každý krok.',
    headline: 'Nahrávací studio Praha — dobrá nahrávka začíná u dobrého zachycení zvuku',
    description: 'Stable Records je nahrávací studio 15 minut od centra Prahy (Březová-Oleško). Nahráváme kapely, sólisty, spoken word i podcasty. Ve studiu mám mikrofony, předzesilňovače a převodníky, které ti umožní zachytit zvuk přesně tak, jak má znít. Kombinuju analogové a digitální vybavení — prostě to, co na danou věc sedne nejlíp. Poradím ti, co na tvůj zvuk použít. Kompletní seznam vybavení najdeš na stránce <a href="/vybaveni"><strong>Vybavení studia</strong></a>.',
    price: 'Cena na vyžádání',
    availability: 'Dostupné na vyžádání',
    images: [
      '/images/services/recording.webp',
      '/images/services/drums.webp',
      '/images/services/pavelhrala.webp',
      '/images/services/coffeecab.webp',
    ],
    bullets: [
      'Špičkové mikrofony (např. Neumann, Shure, AKG)',
      'Vysoce kvalitní preampy a kompresory (např. Universal Audio, Avalon)',
      'Moderní digitální pracovní stanice (DAW) jako Pro Tools, Logic Pro, Ableton Live',
      'Možnost nahrávání jednotlivých stop i živé kapely',
      'Multi-track recording pro maximální flexibilitu při mixu',
      'Monitoring v reálném čase pro okamžitou zpětnou vazbu',
      'Celý proces ti vysvětlím krok za krokem přímo během nahrávání',
      'Pomůžu s aranžmá a přípravou materiálu před nahráváním',
      'Poradím, jaký mikrofon a nástroj na co použít',
      'Ukážu ti, jak to celé funguje — od náběru po finální export',
    ],
    src: '/images/services/recording.webp',
  },
  {
    id: 'pronajem_studia',
    name: 'Pronájem studia',
    seoTitle: 'Pronájem studia Praha-západ od 300 Kč/hod — bicí, piano, backline | Stable Records',
    seoDescription: 'Pronájem plně vybaveného nahrávacího studia Praha-západ (Středočeský kraj) — bicí, piano, baskytara, kytary, mikrofony. Od 300 Kč/h. Pro kapely z Prahy, Berouna i okolí.',
    headline: 'Pronájem studia — pracuj na svém, ve svém tempu',
    description: 'Můžeš si pronajmout celé studio i bez mé asistence. Dostaneš k dispozici veškeré vybavení — mikrofony, preampy, aparáty, bicí. Ideální pro producenty nebo kapely, které si chtějí nahrávat samy. Pronájem je na hodiny nebo na celý den. Studio leží v Praha-západ (Středočeský kraj), 15 minut od centra Prahy — klidné prostředí pro soustředěnou práci. Vhodné i pro podcasty a jiné audio projekty.',
    price: 'Cena na vyžádání',
    availability: 'Dostupné na vyžádání',
    images: [
      '/images/services/mixnmaster.webp',
      '/images/services/mix2.webp',
      '/images/services/fender.webp',
      '/images/services/coffeecab.webp',
    ],
    bullets: [
      'Kompletní vybavení — mikrofony, preampy, aparáty, bicí',
      'Pronájem na hodiny nebo celý den',
      'Pro producenty, kapely i podcastery',
      'Ukážu ti, jak s vybavením pracovat',
      'Technická asistence na místě, pokud ji potřebuješ',
      'Akusticky upravené prostory',
      'Klidné prostředí 20 km od Prahy',
      'Možnost ubytování přímo ve studiu',
      'Vhodné i pro podcasty a jiné audio projekty',
    ],
    src: '/images/services/studio.webp',
  },
];

const servicesDataEn = [
  {
    id: 'ubytovani',
    name: 'Accommodation',
    seoTitle: 'Studio Accommodation near Prague | Stable Records — Included in Full Service',
    seoDescription: 'Accommodation for up to 7 people right at the recording studio near Prague. Included in full service — no extra charge. Grill, pool, quiet surroundings 20 km from Prague.',
    headline: 'Accommodation right at the studio — included in full service',
    description: 'Accommodation for up to 7 people right at the studio, included in the full service price at no extra charge. The rooms have everything you need. A garden with a grill and a pool are available — perfect after a full day in the studio.',
    price: 'Price on request',
    availability: 'Available on request',
    images: [
      '/images/ubytko/grill.webp',
      '/images/ubytko/postele.webp',
      '/images/ubytko/koupelna.webp',
      '/images/ubytko/bar.webp',
    ],
    bullets: [
      'Accommodation for up to 7 people',
      'Fully equipped rooms with all the comfort you need',
      'Outdoor grilling',
      'Pool',
      'All-inclusive — catering can be arranged',
      'Privacy and quiet',
      'Ideal for bands and music teams',
      'Right at the studio — no commuting',
    ],
    src: '/images/ubytko/ubytko.webp',
  },
  {
    id: 'mix_mastering',
    name: 'Mix & Mastering',
    seoTitle: 'Mix & Mastering near Prague — 15 Years of Experience | Stable Records',
    seoDescription: 'Professional mix and mastering for bands and solo artists. Over 15 years of experience, own studio 15 min from Prague. Logic, SSL, Universal Audio, Izotope Ozone. Free consultation.',
    headline: 'Mix & mastering — from raw tracks to a finished sound',
    description: 'During the mix I balance each track so every instrument has its space and the whole sounds natural. Mastering is the final step — I prepare the sound so it works on Spotify, in the car and on headphones. <br> Before the mix I carefully clean and edit the tracks — timing, tuning, noise. I work in Logic with plugins from SSL, Universal Audio and Slate Digital. For mastering I use Izotope Ozone 10 and Slate Digital FGX 2. I have been mixing and mastering for over ten years. See the full gear list on the <a href="/en/equipment"><strong>Studio Equipment</strong></a> page.',
    price: 'Price on request',
    availability: 'Available on request',
    images: [
      '/images/services/drums.webp',
      '/images/services/mixnmaster.webp',
      '/images/services/mix.webp',
      '/images/services/mix2.webp',
    ],
    bullets: [
      'Precise editing and mixing of audio tracks',
      'Work in the Logic DAW',
      'Top-tier plugins (Universal Audio, Slate Digital, SSL)',
      'Mastering tools (Izotope Ozone 10, Slate Digital FGX 2)',
      'Professional sound ready for streaming platforms and physical media',
      'More than 10 years of experience with mix and mastering',
      'Emphasis on detail and quality',
      'Happy to walk you through the mix and mastering process if you are curious',
      'Together we find the sound that fits you',
      'I show you what to focus on when listening and how to describe what you want',
    ],
    src: '/images/services/mix.webp',
  },
  {
    id: 'nahravani',
    name: 'Recording',
    seoTitle: 'Band & Music Recording Prague — Stable Records Studio',
    seoDescription: 'Recording bands, vocals, drums and full ensembles in a fully-equipped studio 15 min from Prague. Neumann U87, acoustically treated room, band accommodation. Great for your first session — we\'ll guide you through every step.',
    headline: 'Recording studio near Prague — a good recording starts with capturing the sound right',
    description: 'Stable Records is a recording studio 15 minutes from the centre of Prague (Březová-Oleško). We record bands, solo artists, spoken word and podcasts. The studio has microphones, preamps and converters that let you capture the sound exactly as it should be. I combine analogue and digital gear — whatever fits the job best. I will advise what to use for your sound. The full equipment list is on the <a href="/en/equipment"><strong>Studio Equipment</strong></a> page.',
    price: 'Price on request',
    availability: 'Available on request',
    images: [
      '/images/services/recording.webp',
      '/images/services/drums.webp',
      '/images/services/pavelhrala.webp',
      '/images/services/coffeecab.webp',
    ],
    bullets: [
      'Top-tier microphones (e.g. Neumann, Shure, AKG)',
      'High-quality preamps and compressors (e.g. Universal Audio, Avalon)',
      'Modern DAWs like Pro Tools, Logic Pro, Ableton Live',
      'Recording of individual tracks or a live band',
      'Multi-track recording for maximum flexibility during the mix',
      'Real-time monitoring for immediate feedback',
      'I explain the whole process step by step during the session',
      'I help with arrangement and material prep before recording',
      'I advise which microphone and instrument to use for what',
      'I show you how it all works — from tracking to the final export',
    ],
    src: '/images/services/recording.webp',
  },
  {
    id: 'podcast',
    name: 'Podcast Recording',
    seoTitle: 'Podcast Recording Prague — clean voice sound from the first take | Stable Records',
    seoDescription: 'Professional podcast recording 15 min from Prague. Acoustically treated room, quality microphones, editing and final mix in one place. Great for interviews, solo episodes and branded shows.',
    headline: 'Podcast recording near Prague — clear voice, calm workflow, pro result',
    description: 'Need a podcast that sounds clean, clear and reliable on Spotify, Apple Podcasts and YouTube? At Stable Records, we record spoken word in an acoustically treated room with professional microphones and monitoring. We can handle the full process from recording to editing and final mix, or just help with the part you need. See current rates on the <a href="/en/pricing"><strong>Pricing</strong></a> page.',
    price: 'Price on request',
    availability: 'Available on request',
    images: [
      '/images/services/studio.webp',
      '/images/services/mixnmaster.webp',
      '/images/services/coffeecab.webp',
      '/images/services/pavelhrala.webp',
    ],
    bullets: [
      'Podcast recording in an acoustically treated room',
      'Ideal for solo episodes, interviews and branded content',
      'Professional voice mics and clean signal chain',
      'Real-time headphone monitoring',
      'Editing of pauses, filler words and unwanted noise',
      'Final mix and loudness-ready master for platforms',
      'Support with recording workflow and episode prep',
      'Fast turnaround and predictable communication',
      'Easy access 15 min from Prague',
      'Accommodation available for longer sessions',
    ],
    src: '/images/services/studio.webp',
  },
  {
    id: 'pronajem_studia',
    name: 'Studio Rental',
    seoTitle: 'Studio Rental near Prague from 300 CZK/hr — Drums, Piano, Backline | Stable Records',
    seoDescription: 'Rent a fully equipped recording studio 15 min from Prague. Drums, piano, guitar amps, Neumann microphones, Universal Audio preamps. From 300 CZK/hr. Ideal for rehearsals and recording.',
    headline: 'Studio rental — work on your project, at your own pace',
    description: 'You can rent the whole studio even without my assistance. You get access to all the gear — microphones, preamps, amps, drums. Ideal for producers or bands who want to record on their own. Rental is hourly or for the full day. The studio is also suitable for podcasts and other audio projects.',
    price: 'Price on request',
    availability: 'Available on request',
    images: [
      '/images/services/mixnmaster.webp',
      '/images/services/mix2.webp',
      '/images/services/fender.webp',
      '/images/services/coffeecab.webp',
    ],
    bullets: [
      'Complete equipment — microphones, preamps, amps, drums',
      'Rental by the hour or for the full day',
      'For producers, bands and podcasters',
      'I will show you how to work with the gear',
      'On-site technical assistance if you need it',
      'Acoustically treated rooms',
      'Quiet surroundings 20 km from Prague',
      'Accommodation right at the studio available',
      'Also suitable for podcasts and other audio projects',
    ],
    src: '/images/services/studio.webp',
  },
  {
    id: 'podcast',
    name: 'Nahrávání podcastu',
    seoTitle: 'Nahrávání podcastu Praha — čistý hlas od první nahrávky | Stable Records',
    seoDescription: 'Profesionální nahrávání podcastu 15 min od Prahy. Akusticky řešená místnost, kvalitní mikrofony, editace i finální mix na jednom místě. Ideální pro rozhovory i sólové epizody.',
    headline: 'Nahrávání podcastu u Prahy — čistý hlas, klidný průběh, profi výsledek',
    description: 'Chceš podcast, který bude znít čistě a čitelně na Spotify, Apple Podcasts i YouTube? Ve Stable Records nahráváme spoken word v akusticky řešené místnosti s profesionálními mikrofony a monitoringem. Můžeme zajistit celý proces od nahrávání po editaci a finální mix, nebo pomoct jen s částí, kterou potřebuješ. Aktuální ceny najdeš na stránce <a href="/cenik"><strong>Ceník</strong></a>.',
    price: 'Cena na vyžádání',
    availability: 'Dostupné na vyžádání',
    images: [
      '/images/services/studio.webp',
      '/images/services/mixnmaster.webp',
      '/images/services/coffeecab.webp',
      '/images/services/pavelhrala.webp',
    ],
    bullets: [
      'Nahrávání podcastu v akusticky řešené místnosti',
      'Vhodné pro rozhovory, sólové epizody i branded obsah',
      'Kvalitní hlasové mikrofony a čistý signál',
      'Monitoring ve sluchátkách v reálném čase',
      'Editace pauz, přeřeků a nežádoucích ruchů',
      'Finální mix a master připravený pro platformy',
      'Pomoc s workflow a přípravou epizod',
      'Rychlé dodání a jasná domluva',
      'Snadná dostupnost 15 min od Prahy',
      'Možnost ubytování při delších sessions',
    ],
    src: '/images/services/studio.webp',
  },
];

/**
 * Vrátí pole služeb v požadovaném jazyce.
 * @param {string} lang — 'cs' | 'en' (fallback 'cs')
 */
export const getServicesData = (lang) =>
  lang === 'en' ? servicesDataEn : servicesDataCs;

export { servicesDataCs, servicesDataEn };

// Zpětná kompatibilita — default export je CS verze (existující testy + případné
// přímé importy bez znalosti jazyka).
export default servicesDataCs;
